/**
 * User persona determines feature access level.
 * Derived from token claims with priority: Admin > Planner > Employee
 */
export type Persona = 'Admin' | 'Planner' | 'Employee';

/**
 * Role claim values expected in the Entra ID token.
 * These must match the App Roles configured in Azure App Registration.
 */
export const ROLE_CLAIMS = {
  ADMIN: 'EGEM.Admin',
  PLANNER: 'EGEM.Planner',
} as const;

/**
 * Represents an authenticated user in the GEM application.
 * Populated from MSAL account info and token claims.
 */
export interface AuthenticatedUser {
  /** Unique user identifier from Entra ID (localAccountId) */
  readonly id: string;

  /** User's email address (from username claim) */
  readonly email: string;

  /** User's display name */
  readonly displayName: string;

  /** Determined access level based on token roles */
  readonly persona: Persona;

  /** Raw role claims from the ID token */
  readonly roles: readonly string[];
}

/**
 * Normalized authentication error structure.
 * Maps MSAL errors to user-friendly messages.
 */
export interface AuthError {
  /** MSAL error code (e.g., "login_required", "interaction_required") */
  readonly code: string;

  /** User-friendly error message (safe to display in UI) */
  readonly message: string;

  /** Unix timestamp when the error occurred */
  readonly timestamp: number;
}

/**
 * Redux state shape for the authentication slice.
 */
export interface AuthState {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;

  /** Whether an authentication operation is in progress */
  isLoading: boolean;

  /** Current authenticated user or null if not authenticated */
  user: AuthenticatedUser | null;

  /** Last authentication error or null if no error */
  error: AuthError | null;

  /** Unix timestamp of last user activity (for idle timeout tracking) */
  lastActivity: number | null;

  /** Unix timestamp when the session will expire */
  sessionExpiresAt: number | null;
}

/**
 * Environment-specific MSAL configuration.
 * Values are loaded from VITE_* environment variables.
 */
export interface MsalEnvironmentConfig {
  /** Azure App Registration Client ID */
  readonly clientId: string;

  /** Entra ID authority URL (e.g., https://login.microsoftonline.com/{tenant}) */
  readonly authority: string;

  /** URI to redirect to after login */
  readonly redirectUri: string;

  /** URI to redirect to after logout */
  readonly postLogoutRedirectUri: string;
}

/**
 * API scopes configuration for token acquisition.
 */
export interface ApiScopes {
  /** Scopes for backend API access */
  readonly api: readonly string[];

  /** Scopes for Microsoft Graph access (if needed) */
  readonly graph: readonly string[];
}

/**
 * Payload for setting user after successful authentication.
 */
export interface SetUserPayload {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly persona: Persona;
  readonly roles: readonly string[];
}

/**
 * Payload for authentication error actions.
 */
export interface SetAuthErrorPayload {
  readonly code: string;
  readonly message: string;
}

/**
 * Props for persona-based route protection.
 */
export interface PersonaGuardProps {
  /** Personas allowed to access the protected content */
  readonly allowedPersonas: readonly Persona[];

  /** Content to render if user has required persona */
  readonly children: React.ReactNode;

  /** Optional fallback to render if access is denied */
  readonly fallback?: React.ReactNode;
}

/**
 * Props for authentication guard component.
 */
export interface AuthGuardProps {
  /** Content to render if user is authenticated */
  readonly children: React.ReactNode;

  /** Optional loading component during auth check */
  readonly loadingComponent?: React.ReactNode;
}

/**
 * Return type for the useAuth hook.
 */
export interface UseAuthResult {
  /** Whether the user is authenticated */
  readonly isAuthenticated: boolean;

  /** Whether authentication is in progress */
  readonly isLoading: boolean;

  /** Current user or null */
  readonly user: AuthenticatedUser | null;

  /** Current error or null */
  readonly error: AuthError | null;

  /** Trigger login flow */
  readonly login: () => Promise<void>;

  /** Trigger logout flow */
  readonly logout: () => Promise<void>;
}

/**
 * Return type for the usePersona hook.
 */
export interface UsePersonaResult {
  /** Current user's persona */
  readonly persona: Persona;

  /** Whether user has Admin persona */
  readonly isAdmin: boolean;

  /** Whether user has Planner persona */
  readonly isPlanner: boolean;

  /** Whether user has Employee persona (default) */
  readonly isEmployee: boolean;

  /** Check if user has any of the specified personas */
  readonly hasPersona: (personas: Persona | Persona[]) => boolean;
}
